import React, { useState, useEffect, useContext, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams } from "react-router-dom";
import { flushSync } from 'react-dom';

import {
  isWidgetLocked,
  useEditorSession, useSelectedWidgetSelector, useWidgetChildrenSelector, useWidgetSelector
} from './hooks/useEditorSession';

import {
  updateGuidelines, selectWidget, selectAllWidgets, deleteWidgets,
  undo, redo,
  attachWidgetToContainer,
  actionGroup, toggleEditMode, bulkUpdateWidgetLayouts, applyWidgetPropertyChanges,
  cloneWidgets,
  selectPageWidget,
  updateWidgetProperty,
  clonePages,
} from 'actions/editor';
import { fetchCustomFields } from 'actions/fields/fields';
import { fetchCustomFormFields } from 'actions/formFields';
import { CONTENT, FORM, GROUP, HOTSPOT, HTML, IFRAME, LIVE_ELEMENT, MENU, MENU_CONTAINER, MODAL, SECTION, VIDEO, BUTTON, IMAGE, LINE, SHAPE, TEXT, RECTANGLE, CONTENT_CARD, SECTION_COLUMN, LIVE_ELEMENT_CONTAINER, PAGE, ANCHOR, CAROUSEL_CARD, CAROUSEL, MENU_DRAGGABLE_CONTAINER, CONTENT_PAGINATION_BUTTON, CONTENT_EMPTY_MESSAGE, CONTENT_LOADING, CONTENT_FILTER_CONTAINER, FORM_FIELD, FORM_NUMBER, FORM_TEXTAREA, FORM_DROPDOWN_LIST, FORM_CHECK_LIST, FORM_CHECK_BOX, FORM_SUBMIT, OTP_INPUT, OTP_FORM_SUBMIT, OTP_RESEND, FORM_STEP, EXTERNAL_FORM, CONTAINER } from 'constants/widgetTypes';
import { createGroupWidget, unGroupWidget } from 'actions/group';

import _ from 'lodash';
import cx from 'classnames';

import { Frame } from 'scenejs';
import Guides from '@scena/react-guides';
import Moveable, { getElementInfo } from "react-moveable";
import Selecto from 'react-selecto';
import { GlobalHotKeys, HotKeys } from "react-hotkeys";
import { Widget } from './widgets/core';
import { GroupToolBarViewable, ToolBarViewable } from './components/toolbar';
import { WidgetRegistry } from './widgets/registry';
import { getCardFlexConfig } from './widgets/carousel/card/flexConfig';
import { allColumnsInFlexMode, resolveFlexLayout, flexBaseDefaults, breaksOutOfFlexFlow } from './widgets/flex/flexContext';
import Layers from "components/Editor/components/layers";
import Tools from './components/tools';
import Menu from './components/menu';
import ClipboardManager from './components/clipboard';
import textStyles from './widgets/text/text.module.scss';

import { isMacOs } from 'react-device-detect';
import { SlideRender } from './widgets/slideRender';
import { PaletteContext } from 'components/Palette';
import { updatePage } from 'actions/pages';
import { updatePaletteComponent } from 'actions/brandKit/components';
import storyboardRoleService from 'services/storyboardRoles.service';
import { updateGating } from 'actions/gating';
import { updateLiveElement } from 'actions/live_elements';
import {VariablesLayer} from 'components/Editor/components/variables';
import { getWidgetType } from './helper/widget';

import { BrandThemeManager, Fonts, FormContext, ParallaxContext, WidgetActions, Widgets, WidgetsContext } from '@paperflite-marketing/cleverstory-commons';
import Portal from 'components/Portal';
import StoryboardContext from 'components/Storyboard/context';
import SegmentRender from './components/segmentRender';
import JourneyNudge from './components/nudges';
import { SEGMENT_STORYBOARDS, SLIDE_STORYBOARDS, STORYBOARD_TYPES, supportsFrameEditorFeatures } from 'constants/storyboardType';

import { FailureToast } from 'components/Toast';
import { toast } from 'react-toastify';
import {
  blockClipboardOperation,
  blockDeleteOperation,
  blockPasteOperation,
} from 'components/Editor/utils/aiImageEditGuard';
import styles from './editor.module.scss';


export const EditorContext = React.createContext({
  editorSession: null
});

// Stable empty context values — defined once at module level so they never
// change reference between renders, preventing context-triggered re-renders
// on all consumers (bypasses React.memo otherwise).
const EMPTY_FORM_CONTEXT = {};
const EMPTY_WIDGET_ACTIONS = { onBeforeExecution: () => {} };

export const Editor = React.forwardRef(({ id, className, entityType, disabled, isOptimizing, view }, ref) => {
  const dispatch = useDispatch();

  const { id: gatingId } = useParams();

  const scrollContainerRef = React.useRef(null);
  const editorRegistryRef = useRef({});
  const [scrollEl, setScrollElement] = React.useState(null);
  const editorContainerRef = React.useRef(null);
  const [panelVisibility, setPanelVisibility] = useState({
    layers: true,
    formLayers: true
  });
  const [showMenu, setShowMenu] = useState(null);
  const [allowMultipleSegments, setAllowMultipleSegments] = useState(false);
  const [editorRegistry, setEditorRegistry] = useState({});
  const [isTextEditing, setIsTextEditing] = useState({});
  

  const registerEditor = useCallback((id, editor) => {
    editorRegistryRef.current = { ...editorRegistryRef.current, [id]: editor };
    setEditorRegistry(editorRegistryRef.current);
  }, []);

  const unregisterEditor = useCallback((id) => {
    const next = { ...editorRegistryRef.current };
    delete next[id];
    editorRegistryRef.current = next;
    setEditorRegistry(next);
  }, []);

  const setEditingState = useCallback((id, value) => {
    setIsTextEditing(prev => {
      if (prev[id] === value) return prev;
      return { ...prev, [id]: value };
    });
  }, []);

  const editorSession = useEditorSession(id);

  const editorContextValue = useMemo(() => ({
    editorSession, disabled, entityType, editorRegistry,
    allowMultipleSegments, isTextEditing,
    registerEditor, unregisterEditor, setEditingState,
  }), [
    editorSession, disabled, entityType, editorRegistry,
    allowMultipleSegments, isTextEditing,
    registerEditor, unregisterEditor, setEditingState
  ]);


  const storyboard = useContext(StoryboardContext);
  const storyboardType = _.get(storyboard, 'type');
  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item", null));
  const canTriggerNudge = useSelector((state) => _.get(state, "workspaces.triggerJourneyNudge", false));
  const promptError = useSelector(state => _.get(state, `${id}.present.promptError`));
  const isPromptError = _.get(promptError, 'error', false);
  const promptErrorMessage = _.get(promptError, "message", "");
  const [selectedWidgets, setSelectedWidgets] = useState([]);
  const [editorWidth, setEditorWidth] = useState('100%');

  const activePageSection = useSelector(state => _.get(state, `${id}.present.interactive.active`));
  var widgets = useWidgetChildrenSelector(id, null);
  const selectedWidget = useSelectedWidgetSelector(id);
  
  const editMode = useSelector(state => {
    return _.get(state, `${id}.present.editMode`) === selectedWidget[0]?.uid
  });

  const breakpoint = useSelector(state => _.get(state, `${id}.present.breakpoint`));
  
  const selected = useSelector(state => _.get(state, `${id}.present.selected`) || []);

  const fontThemes = useSelector(state => _.get(state, `${id}.present.themes`));

  const fonts = useSelector(state => _.get(state, `${id}.present.fonts`) || []);
  Fonts.useFont(fonts);

  const pageId = useSelector(state => _.get(state, `storyboards.storyboard.item.page.id`, []))

  const page = useSelector(state => _.get(state, `pages.page.${pageId}.item`, []));

  const showLayers = useSelector(state => _.get(state, `${id}.present.tools.layers`));
  const formLayers = useSelector(state => _.get(state, `${id}.present.tools.formLayers`, false));
  const formLayerProps = useSelector(state => _.get(state, `${id}.present.tools.formLayerProps`, {}));

  const showTools = useSelector(state => _.get(state, `${id}.present.tools.toolbar`));
  const showVariables = useSelector(state => _.get(state, `${id}.present.tools.variables`));
  const previousSelected = useRef([]);
  const presentWidgets = useSelector(state => _.get(state, `${id}.present.widgets`) || []);

  const formWidgetsLength = useMemo(
    () => presentWidgets.filter(w => w.type === FORM).length,
    [presentWidgets]
  );
  const progressiveGatingEnabled = useSelector((state) => _.get(state, `gatings.gating.${gatingId}.item.progressive`, false));

  useEffect(() => {
    setScrollElement(scrollContainerRef.current);
  }, [page]);

  useEffect(() => {
    if (!presentWidgets?.length) return;

    const getWidget = uid => document.getElementById(uid);
    const widgetMap = new Map(presentWidgets.map(w => [w.uid, w])); // O(n) once, O(1) lookups

    selected.forEach(uid => {
      const widget = widgetMap.get(uid);
      if (!widget) return;
  
      const isLocked = isWidgetLocked(presentWidgets, widget, breakpoint);
      const config = WidgetRegistry.moveableConfig(id, widget, breakpoint);
      const widgetElement = getWidget(uid);
  
      if (widgetElement)
        widgetElement.style.cursor =  widget.type === "SECTION" ? "" : !disabled && config.draggable !== false && !isLocked ? "grab" : "";
    });
  
    previousSelected.current?.forEach(uid => {
      if (!selected.includes(uid)) getWidget(uid)?.style.removeProperty("cursor");
    });
  
    if (selected.length) previousSelected.current = selected;
    if (!selectedWidget?.length) return;
  
    selectedWidget.forEach(({ uid }) => {
      const widgetElement = getWidget(uid);
      if (!widgetElement) return;
  
      if (editMode) {
        widgetElement.style.cursor = '';
      }
    });
  }, [selected, disabled, editMode]);
  
  useEffect(() => {
    const container = document.querySelector(`#${editorSession.id} .editor_widget_scroll_container`);

    if (!container){
      return;
    }

    const handleRightClick = (e) => {

      const target = e.target.closest(`[data-selectable="true"]`);
      if (!target) return;
      e.preventDefault(); 
      editorSession.selecto.current.clickTarget(e, target);
      setShowMenu({ positionX: e.clientX, positionY: e.clientY });
    };

    container.addEventListener("contextmenu", handleRightClick);

    return () => {
      container.removeEventListener("contextmenu", handleRightClick);
    };
  }, [editorSession]);

  useEffect(() => {
    if (!isPromptError) return;
  
    toast(
        <FailureToast
          heading={`Failure`}
          description={promptErrorMessage}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
    );

  }, [isPromptError]);

  useEffect(() => {
    if(storyboard && _.get(storyboard, 'type') === "RESOURCE_CENTER") {
      dispatch(fetchCustomFields(_.get(storyboard, 'id')))
    }
  }, [storyboard])

  useEffect(() => {
    if(workspace) {
      handleFetchFormFields([], true);
      handleFetchFormFields(['boolean_field'], false)
    }
  }, [workspace])

  useEffect(() => {
    const container = scrollContainerRef.current;
    container.addEventListener('scroll', () => {
      if (container.scrollLeft > 0) {
        container.scrollLeft = 0;
      }
    });
    return () => {
      container.removeEventListener('scroll', () => {});
    };
  }, []);

  useEffect(() => {
    const handlePaste = async (event) => {
      const activeElement = document.activeElement;
      const isInputField = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' || 
        activeElement.contentEditable === 'true'
      );
  
      if (isInputField) {
        return;
      }

      if (blockPasteOperation(presentWidgets)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
  
      event.preventDefault();
      event.stopPropagation();

      const clipboardData = event.clipboardData || event.originalEvent?.clipboardData;
        
      const clipboardLocalData = window.localStorage.getItem('CLIPBOARD');
      const request = JSON.parse(clipboardLocalData || '{}');
      const hasForm = request?.widgets?.some(w => w.type === FORM || w.type === EXTERNAL_FORM);

      if (
        entityType === "gating" &&
        formWidgetsLength >= 1 &&
        progressiveGatingEnabled && 
        hasForm
      ) {
        return toast(
          <FailureToast heading={`Error`} description={`Progressive gating is enabled. Only one form widget is allowed`} />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
      }


      try {
        const items = clipboardData?.items;
        const files = [];
        let hasFiles = false;
  
        if (items) {
          for (const item of items) {
            if (item.kind === 'file') {
              const file = item.getAsFile();
              if (file && !file.type.startsWith('text/')) {
                files.push(file);
                hasFiles = true;
              }
            }
          }
        }
  
        if (hasFiles && files.length > 0) {
          editorSession.eventBus.emit('pasteMedia', { files });
          return;
        }
  
        try {
          const clipboardItems = await navigator.clipboard.read();
          for (const item of clipboardItems) {
            for (const type of item.types) {
              if (!type.startsWith('text/')) {
                const blob = await item.getType(type);
                const file = new File([blob], `pasted-image.${type.split('/')[1]}`, { type });
                files.push(file);
                hasFiles = true;
              }
            }
          }
  
          if (hasFiles && files.length > 0) {
            editorSession.eventBus.emit('pasteMedia', { files });
            return;
          }
        } catch (clipboardError) {
          console.log('Clipboard API access failed:', clipboardError);
        }
  
        editorSession.eventBus.emit('paste');
        
      } catch (error) {
        editorSession.eventBus.emit('paste');
      }
    };

    const handleCopyOrCut = (event) => {
      const activeElement = document.activeElement;
      const isInputField = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' || 
        activeElement.contentEditable === 'true'
      );
  
      if (isInputField) {
        return;
      }

      const selectedForGuard = selected
        .map((uid) => presentWidgets.find((w) => w.uid === uid))
        .filter(Boolean);
      if (blockClipboardOperation(selectedForGuard)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const clipboardData = event.clipboardData || event.originalEvent?.clipboardData;
      const widgets = selectedWidgets.filter(widget => {
        const template = _.get(widget, 'template');
        if (template) {
          return false;
        }
        const moveableConfig = WidgetRegistry.moveableConfig(editorSession, widget);
        return _.get(moveableConfig, 'cloneable', true);
      }).map(el => el.uid);

      if (_.size(widgets) > 0 && clipboardData) {
        const dataToCopy = JSON.stringify(widgets);
        clipboardData.setData('text/plain', dataToCopy);
      }
    }
  
    document.addEventListener('paste', handlePaste);
    document.addEventListener('copy', handleCopyOrCut);
    document.addEventListener('cut', handleCopyOrCut);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('copy', handleCopyOrCut);
      document.removeEventListener('cut', handleCopyOrCut);
    };
  }, [editorSession, formWidgetsLength, progressiveGatingEnabled, entityType, presentWidgets, selected]);

  useEffect(() => {
    const handleDragOver = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.classList.add('drag-over');
    };
  
    const handleDragLeave = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.classList.remove('drag-over');
    };
  
    const handleDrop = (event) => {
      event.preventDefault();
      event.stopPropagation();
      
      event.currentTarget.classList.remove('drag-over');
      
      const files = Array.from(event.dataTransfer.files);

      if (files.length > 0) {
        editorSession.eventBus.emit('dropMedia', { files: files });
      }
    };
  
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('dragover', handleDragOver);
      container.addEventListener('dragleave', handleDragLeave);
      container.addEventListener('drop', handleDrop);
  
      return () => {
        container.removeEventListener('dragover', handleDragOver);
        container.removeEventListener('dragleave', handleDragLeave);
        container.removeEventListener('drop', handleDrop);
      };
    }
  }, [editorSession, scrollContainerRef]);

  useEffect(() => {
    setScrollElement(scrollContainerRef.current);
  }, [scrollContainerRef]);

  useEffect(() => {
    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    }
  }, [editorContainerRef.current]);

  useEffect(() => {
    setShowMenu(null);
  },[breakpoint]);
  
  useEffect(() => {
    let lastWidth = window.innerWidth;
    let lastHeight = window.innerHeight;
    let lastZoom = window.devicePixelRatio;

    const handleViewportChange = () => {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      const currentZoom = window.devicePixelRatio;

      const widthChanged = Math.abs(currentWidth - lastWidth) > 50;
      const heightChanged = Math.abs(currentHeight - lastHeight) > 50;
      const zoomChanged = Math.abs(currentZoom - lastZoom) > 0.01;

      if (widthChanged || heightChanged || zoomChanged) {
        setPanelVisibility({
          layers: false,
          formLayers: false,
        });

        setTimeout(() => {
          setPanelVisibility({
            layers: showLayers && !disabled,
            formLayers: formLayers && !disabled
          });
        }, 100);

        lastWidth = currentWidth;
        lastHeight = currentHeight;
        lastZoom = currentZoom;
      }
    };

    let timeoutId;
    const debouncedHandler = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleViewportChange, 200);
    };

    window.addEventListener('resize', debouncedHandler);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedHandler);
    };
  }, [showLayers, formLayers, showTools, showVariables, disabled]);

  useEffect(() => {
    setPanelVisibility({
      layers: showLayers && !disabled,
      formLayers: formLayers && !disabled
    });
  }, [showLayers, formLayers, disabled]);

  useEffect(() => {
    if (storyboardType && SEGMENT_STORYBOARDS.includes(storyboardType)) {
      setAllowMultipleSegments(true);
    }
  }, [storyboardType])

  const isFrameOnlySelection = useMemo(() => {
    if (!supportsFrameEditorFeatures(entityType, storyboardType) || !selected.length) {
      return false;
    }
    const widgetMap = new Map(presentWidgets.map(w => [w.uid, w]));
    return selected.every(uid => widgetMap.get(uid)?.type === PAGE);
  }, [entityType, storyboardType, selected, presentWidgets]);

  const resolveSelectedTargets = useCallback(() => {
    const editorRoot = document.getElementById(id); // O(1) container lookup
    var targets = (selected || []).map(widget => {
      return editorRoot?.querySelector(`.editor_widget_renderer #${widget}`) ?? null;
    }).filter(element => element != null);

    const beforeFilter = targets.map(t => t.id);
    targets = targets.filter(target => {
      return targets.every(parentTarget => {
        return parentTarget === target || !parentTarget.contains(target);
      });
    });

    if (isFrameOnlySelection) {
      targets = [];
    }

    setSelectedWidgets(targets);
    editorSession.selecto.current?.setSelectedTargets(targets);
    editorSession.moveableData.setSelectedTargets(targets);
    editorSession.eventBus.trigger("setSelectedTargets");
  }, [id, selected, editorSession, isFrameOnlySelection]);

  useEffect(() => {
    resolveSelectedTargets();
  }, [selected, isFrameOnlySelection]);

  useEffect(() => {
    editorSession.eventBus.on("refreshTargets", resolveSelectedTargets);
    return () => editorSession.eventBus.off("refreshTargets", resolveSelectedTargets);
  }, [resolveSelectedTargets, editorSession]);
  
  const handleFetchFormFields = useCallback((ignoreTypes, defaultFields) => {
    dispatch(fetchCustomFormFields({
      workspace: _.get(workspace, 'id'),
      defaultFields,
      query: "",
      ignoreTypes,
      page: 0,
      limit: 50,
      sort: "created_date"
    }));
  }, [dispatch, workspace]);

  const handleWindowResize = useCallback(() => {
    if (editorContainerRef.current) {
      setEditorWidth(`${editorContainerRef.current.offsetWidth}px`);
    }
  }, []);
  
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    setShowMenu({ positionX: e.clientX, positionY: e.clientY });
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (e.button === 0) setShowMenu(null);
  }, []);

  const pageRender = useCallback(() => {
    switch (storyboardType) {
      case "LANDING_PAGE":
      case "RESOURCE_CENTER":
      case STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2:
        if (!["gating"].includes(entityType)) {
          return (
            <div className={cx(styles.pageWrapper, { [styles.mobileWrapper]: breakpoint === "mobile" }, "editor-page-wrapper")}>
              <SegmentRender widgets={widgets} entityType={entityType} />
            </div>
          );
        }
        break;
      case "SPATIAL":
        return <SlideRender locked={disabled} entityType={entityType} />;
      case "FLIP_BOOK":
        return <SlideRender locked={disabled} entityType={entityType} />;
      default:
        break;
    }
    return widgets
      .filter(item => item.uid === activePageSection)
      .map(widget => <Widget key={widget.uid} widgetProps={widget} entityType={entityType} />);
  }, [storyboardType, entityType, breakpoint, widgets, disabled, activePageSection]);
  

  return (
    <EditorContext.Provider value={editorContextValue}>
      <BrandThemeManager className={`.${textStyles.text}`} themes={fontThemes} />
      <div id={id} className={cx(styles.editor, className)}>
        {!disabled &&
          <KeyManager editorSession={id} />
        }

        <RulerGuide storyboardType={storyboardType} editorSession={id} scrollContainer={scrollContainerRef} editorWidth={editorWidth} disabled={disabled} />

        <div className={cx(styles.renderer, 'editor_widget_renderer')}>
          <style>
            {`
              .editor_widget_viewer {
                --editor-width: ${editorWidth};
              }
            `}
          </style>
          <div ref={editorContainerRef} className={cx(styles.viewer, 'editor_widget_viewer')}  onContextMenu={handleContextMenu} onMouseDown={handleMouseDown}>
            <div ref={scrollContainerRef}
              className={cx(styles.devices, { [styles.hideScroll]: !isMacOs }, { [styles.sticky] : showMenu }, { [styles.segment]: SEGMENT_STORYBOARDS.includes(storyboardType) }, styles[breakpoint], breakpoint, 'editor_widget_scroll_container')}
            >
                <ParallaxContext scrollRef={scrollEl}>
                  <MoveableManager
                    editorSession={id}
                    ref={editorSession.moveableManager}
                    selectedWidgets={selectedWidgets}
                    disabled={disabled}
                    entityType={entityType}
                    selectedWidget={selectedWidget}
                  />
                  {pageRender()}
                </ParallaxContext>
            </div>
          </div>

          <Selecto
            ref={editorSession.selecto}
            getElementRect={getElementInfo}
            dragContainer={`#${editorSession.id} .${styles.renderer}`}
            selectableTargets={[`#${editorSession.id} .widget[data-selectable="true"]`]}
            selectByClick={!disabled}
            selectFromInside={false}
            toggleContinueSelect={[["ctrl"], ["meta"]]}
            preventDefault={true}
            preventDragFromInside={false}
            onDragStart={e => {
              const moveable = editorSession.moveableManager.current;
              const target = e.inputEvent.target;

              const targets = selected.map(id => {
                return document.querySelector(`#${id}`);
              }).filter(item => item != null);

              const widgetToolbar = target.closest('.moveable-widget-toolbar');
              const namePill = target.closest('.moveable-name-pill');
              if (
                (e.inputEvent.type === "touchstart" && e.isTrusted)
                || moveable?.isMoveableElement(target)
                || moveable?.isMoveableElement(widgetToolbar)
                || namePill
                || targets.some(t => t === target || t.contains(target))
              ) {
                e.stop();
                return;
              }
            }}
            onSelectEnd={(e) => {
              if (e.isDragStart) {
                e.inputEvent.preventDefault();
              }

              let selected = e.selected.filter(el => el.getAttribute("data-widget-type") !== PAGE);
              const ignoreTypes = [MENU_CONTAINER, MENU_DRAGGABLE_CONTAINER, CONTENT_PAGINATION_BUTTON, CONTENT_EMPTY_MESSAGE, CONTENT_LOADING, CONTENT_FILTER_CONTAINER, FORM_FIELD, FORM_NUMBER, FORM_TEXTAREA, FORM_DROPDOWN_LIST, FORM_CHECK_LIST, FORM_CHECK_BOX, FORM_SUBMIT, OTP_INPUT, OTP_FORM_SUBMIT, OTP_RESEND];

              if (selected?.length > 1) {
                const groupParentTypes = [CONTENT_CARD, FORM_STEP];
              
                // Precompute parent types
                const parentTypes = selected.map(item => {
                  const parent = item.parentElement.closest('div[data-widget-type]');
                  return parent ? getWidgetType(editorSession.id, parent.id) : null;
                });
              
                // Check if all selected elements belong to the same allowed group type
                const insideAnyGroup = groupParentTypes.some(
                  groupType => parentTypes.every(type => type === groupType)
                );
              
                // Check if any item is ignored (based on its own type or parent's)
                const hasIgnored = selected.some(item => {
                  const type = getWidgetType(editorSession.id, item.id);
                  const parent = item.parentElement.closest('div[data-widget-type]');
                  const parentType = parent ? getWidgetType(editorSession.id, parent.id) : null;
                  return ignoreTypes.includes(type) || parentType === CONTENT_CARD;
                });
              
                if (!insideAnyGroup && hasIgnored) {
                  return;
                }
              }              
            
              const selectedIds = selected.map(el => el.id);
              dispatch(selectWidget(id, selectedIds));

              const selectedPage = e.selected.find(el => el.getAttribute("data-widget-type") === PAGE);
              // if (!selectedPage && selected.length === 0 && id) {
              //   dispatch(selectPageWidget(id, null));
              // }

              if (!e.isDragStart) {
                return;
              }

              setTimeout(() => {
                editorSession.moveableManager.current.dragStart(e.inputEvent);
              }, 0);

            }}
          ></Selecto>
          <div className={cx(styles.widgetLayoutHelper, 'widget_layout_helper')}></div>
          <ReparentIndicator />
          <DevicePreviewContainer editorSession={id} widgets={widgets} />
        </div>
      </div>

      {panelVisibility.layers && !isOptimizing &&
        <Layers root={activePageSection} />
      }

      {panelVisibility.formLayers && !isOptimizing &&
        <Layers root={activePageSection} formLayerProps={formLayerProps}/>
      }

      {showTools && !disabled && !isOptimizing &&
        <Tools />
      }

      {showMenu && !disabled && !isOptimizing &&
         <Menu positionX={showMenu.positionX} positionY={showMenu.positionY} setShowMenu={setShowMenu} />}

      {showVariables && !disabled && !isOptimizing &&
        <VariablesLayer/>
      }

      {!disabled && !isOptimizing &&
        <ClipboardManager />
      }

      {(!workspace.journeyNudge && canTriggerNudge && !SLIDE_STORYBOARDS.includes(storyboardType)) &&
        <JourneyNudge
          storyboard={storyboard}
          workspace={workspace}
        />
      }
    </EditorContext.Provider>
  );
});

function RulerGuide({ editorSession, scrollContainer, storyboardType, editorWidth, disabled }) {
  const dispatch = useDispatch();
  const horizontalGuide = React.useRef(null);
  const verticalGuide = React.useRef(null);

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const horizontal = useSelector(state => _.get(state, `${editorSession}.present.${breakpoint}.guides.horizontal`));
  const vertical = useSelector(state => _.get(state, `${editorSession}.present.${breakpoint}.guides.vertical`));
  const spatialSettingMode = useSelector(state => _.get(state, `${editorSession}.present.spatialSettingMode`));
  const flipbookSettingMode = useSelector(state => _.get(state, `${editorSession}.present.flipbookSettingMode`));

  const showRulers = useSelector((state) => _.get(state, `${editorSession}.present.tools.rulers`));

  const activePage = useSelector(state => {
    const active = _.get(state, `${editorSession}.present.interactive.active`);
    if (!active) {
      return null;
    }

    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    const page = widgets.find(item => item.uid === active);
    return page;
  });

  const pageWidth = SLIDE_STORYBOARDS.includes(storyboardType) ? parseInt((_.get(activePage, `properties.${breakpoint}.layout.width`) || '1200').replace('px', ''), 10)
    : _.get(activePage, `properties.${breakpoint}.container.minWidth`) || 1200;

  useEffect(() => {
    updateVerticalOffset();
    if (pageWidth) {
      calculateHorizontalOffset();
    }

    window.addEventListener('resize', handleWindowResize);
    scrollContainer.current.addEventListener('scroll', updateVerticalOffset);

    return () => {
      if (scrollContainer.current) {
        scrollContainer.current.removeEventListener('scroll', updateVerticalOffset);
      }

      window.removeEventListener('resize', handleWindowResize);
    }
  }, [showRulers, spatialSettingMode, flipbookSettingMode]);

  useEffect(() => {
    if (pageWidth) {
      calculateHorizontalOffset();
    }
  }, [pageWidth, editorWidth, spatialSettingMode, flipbookSettingMode]);

  useEffect(() => {
    updateVerticalOffset();
    if (pageWidth) {
      calculateHorizontalOffset();
    }

  }, [breakpoint]);

  const handleWindowResize = () => {
    calculateHorizontalOffset();
    updateVerticalOffset();
  }

  const updateVerticalOffset = useCallback(() => {
    if (!showRulers || spatialSettingMode === 'pageDetails' || flipbookSettingMode === 'pageDetails' || !horizontalGuide.current) return;
    const scrollTop = scrollContainer.current.scrollTop;
    verticalGuide.current.scroll(scrollTop - 100);
    horizontalGuide.current.scrollGuides(scrollTop - 100);
    verticalGuide.current.resize();
  }, [showRulers, spatialSettingMode, flipbookSettingMode, scrollContainer]);
   
  const calculateHorizontalOffset = useCallback(() => {
    if (!showRulers || !pageWidth || spatialSettingMode === 'pageDetails' || flipbookSettingMode === 'pageDetails' || !horizontalGuide.current) return;
    const rect = scrollContainer.current.getBoundingClientRect();
    const offsetX = (rect.width - pageWidth) / 2;
    horizontalGuide.current.scroll(-offsetX);
    verticalGuide.current.scrollGuides(-offsetX);
    horizontalGuide.current.resize();
  }, [showRulers, pageWidth, spatialSettingMode, flipbookSettingMode, scrollContainer]);

  if (!showRulers || spatialSettingMode === 'pageDetails' || flipbookSettingMode === 'pageDetails') {
    return false;
  }

  return (
    <>
      <Guides ref={horizontalGuide}
        type="horizontal"
        unit={100}
        segment={10}
        className={cx(styles.guide, styles.horizontal, {
          [styles.disabled]: !showRulers,
          [styles.glassEffect]: SLIDE_STORYBOARDS.includes(storyboardType)
        })}
        lockGuides={disabled}
        negativeRuler={true}
        displayDragPos={true}
        rulerStyle={{ left: "0px", width: "calc(100% - 22px)", height: "22px" }}
        backgroundColor='rgba(0,0,0,0)'
        lineColor="#a6a6a6"
        textColor="#a6a6a6"
        direction='start'
        mainLineSize={16}
        longLineSize={7}
        shortLineSize={5}
        textOffset={[0, -6]}
        textFormat={(scale) => {
          if (scale < 0) {
            return "";
          }

          if (scale > pageWidth) {
            return "";
          }

          return scale;
        }}
        defaultGuides={horizontal}
        onChangeGuides={e => {
          dispatch(updateGuidelines(editorSession, 'horizontal', breakpoint, e.guides));
        }}
      ></Guides>
      <Guides ref={verticalGuide}
        type="vertical"
        className={cx(styles.guide, styles.vertical, {
          [styles.disabled]: !showRulers,
          [styles.glassEffect]: SLIDE_STORYBOARDS.includes(storyboardType)
        })}
        lockGuides={disabled}
        unit={100}
        segment={10}
        negativeRuler={true}
        displayDragPos={true}
        rulerStyle={{ top: "22px", height: "calc(100% - 22px)", width: "22px" }}
        backgroundColor='rgba(0,0,0,0)'
        lineColor="#a6a6a6"
        textColor="#a6a6a6"
        direction='start'
        mainLineSize={16}
        longLineSize={7}
        shortLineSize={5}
        textOffset={[-6, 0]}
        textFormat={(scale) => {
          if (scale < 0) {
            return "";
          }

          return scale;
        }}
        defaultGuides={vertical}
        onChangeGuides={e => {
          dispatch(updateGuidelines(editorSession, 'vertical', breakpoint, e.guides));
        }}
      ></Guides>
    </>
  );
}

const MoveableManager = React.forwardRef(({ editorSession, selectedWidgets, entityType, disabled, selectedWidget }, ref) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const storyboardType = _.get(useContext(StoryboardContext), 'type', '');
  const moveableData = editorContext.editorSession.moveableData;
  const selecto = editorContext.editorSession.selecto;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const showRulers = useSelector((state) => _.get(state, `${editorSession}.present.tools.rulers`));
  const allWidgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`) || []);
  const allWidgetsMap = React.useMemo(
    () => new Map(allWidgets.map(w => [w.uid, w])),
    [allWidgets]
  );

  const horizontalGuidelines = useSelector(state => _.get(state, `${editorSession}.present.${breakpoint}.guides.horizontal`)) || [];
  const verticalGuidelines = useSelector(state => _.get(state, `${editorSession}.present.${breakpoint}.guides.vertical`)) || [];
  const selectedPage = useSelector(state => _.get(state, `${editorSession}.present.selectedPage`));

  const elementGuidelines = useMemo(
    () => [...moveableData.getTargets()].filter(el => !selectedWidgets.includes(el)),
    [moveableData, selectedWidgets]
  );
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === selectedWidget[0]?.uid
  });
  const [dragTarget, setDragTarget] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState(null);
  const [hasMoved, setHasMoved] = useState(false);
  const dragScrollContainerRef = useRef(null);

  const selectedWidgetEntities = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    const selected = _.get(state, `${editorSession}.present.selected`) || [];
    if (selected.length === 0) return [];
    const selectedSet = new Set(selected);
    return widgets.filter(item => selectedSet.has(item.uid));
  }, shallowEqual);

  const pageParents = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
  
    const parentIds = selectedWidgetEntities.map(widget => widget.parent);
  
    return parentIds.every(parentId => {
      const parentWidget = widgets.find(widget => widget.uid === parentId);
      return parentWidget?.type === PAGE;
    });
  });

  const calculateBounds = useCallback(() => {
    const scrollContainer = document.querySelector(`#${editorSession} .editor_widget_scroll_container`);
    if (!scrollContainer) return null;
    return { top: -(showRulers ? 22 : 0) };
  }, [editorSession, showRulers]);

  const isParentLocked = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];

    return selectedWidgetEntities.some(widget =>
      isWidgetLocked(widgets, widget, breakpoint)
    );
  });

  const isFlexParent = useCallback((parent) => {
    if (!parent) return false;

    let result = false;
    if (parent.type === SECTION_COLUMN) {
      result = _.get(parent, 'properties.desktop.flexBoxEnabled', false);
    } else if (parent.type === CAROUSEL_CARD) {
      result = getCardFlexConfig(parent, breakpoint).flexBoxEnabled;
    } else if (parent.type === CONTAINER) {
      result = _.get(parent, 'properties.desktop.flexBoxEnabled', false);
    } else if (parent.type === PAGE) {
      result = storyboardType !== STORYBOARD_TYPES.INTERACTIVE_CONTENT &&
        _.get(parent, 'properties.desktop.flexBoxEnabled', false);
    }

    return result;
  }, [breakpoint, storyboardType]);

  const isInFlexFlow = useCallback((item) => {
    if (breaksOutOfFlexFlow(item.type)) return false;
    return isFlexParent(allWidgetsMap.get(item.parent));
  }, [allWidgetsMap, isFlexParent]);

  const isContentDrivenSection = useCallback((item) => {
    if (item.type !== SECTION) return false;
    return allColumnsInFlexMode(allWidgets.filter(w => w.parent === item.uid));
  }, [allWidgets]);

  const moveableConfig = useMemo(() => {
    let config = {};
    selectedWidgetEntities.forEach(item => {
      let itemConfig = WidgetRegistry.moveableConfig(editorSession, item, breakpoint);
      itemConfig = _.pick(itemConfig, ['draggable', 'resizable', 'rotatable', 'renderDirections', 'hideDefaultLines', 'bounds']);
      if (isContentDrivenSection(item)) {
        itemConfig = {
          ...itemConfig,
          resizable: false,
          renderDirections: []
        };
      }
      if (disabled || isParentLocked || isInFlexFlow(item)) {
        const { preserveSize, preserveWidth } = WidgetRegistry.flexFlow(item);
        const keepResizable = isInFlexFlow(item) && !disabled && !isParentLocked && (preserveSize || preserveWidth);
        itemConfig = {
          ...itemConfig,
          draggable: (disabled || isParentLocked) ? false : itemConfig.draggable,
          resizable: keepResizable,
          rotatable: false,
          renderDirections: keepResizable ? itemConfig.renderDirections : false
        };
      }
      if (isParentLocked) {
        itemConfig = { ...itemConfig, cloneable: false, deletable: false };
      }
      config = { ...config, ...itemConfig };
    });
    const bounds = calculateBounds();
    if (bounds && !disabled && pageParents) config.bounds = bounds;
    return config;
  }, [selectedWidgetEntities, disabled, isParentLocked, breakpoint, calculateBounds, pageParents, isInFlexFlow, isContentDrivenSection]);
  

  const isVisible = _.get(selectedWidgetEntities[0], `properties.${breakpoint}.visible`) || false;

  const bounds = calculateBounds();
  if (bounds && !disabled && pageParents) {
    moveableConfig.bounds = bounds;
  }

  useEffect(() => {
    setDragTarget(document.querySelector(`#${editorSession} .moveable-drag-target`));
  }, [selectedWidgets])

  const calculateDistance = (pos1, pos2) => {
    const dx = pos2.x - pos1.x;
    const dy = pos2.y - pos1.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const IsTargetInLastContainer = (target) => {
    if (!target) return false;

    const selectedPageElement = document.getElementById(`${selectedPage}`);

    if (!selectedPageElement) return false;

    const containers = selectedPageElement.querySelectorAll('.cs_page_root');
    const lastContainer = containers[containers.length - 1];

    return lastContainer && lastContainer.contains(target);
  }

  const applyDragBoundary = useCallback((target, x, y, event) => {
    const scrollContainer = document.querySelector('.editor-page-wrapper');
    if (!scrollContainer) return false;
   
    const containerRect = scrollContainer.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const scrollTop = scrollContainer.scrollTop;
   
    const containerTop = containerRect.top + scrollTop;
    const containerBottom = containerRect.bottom + scrollTop;
    const targetTop = targetRect.top + scrollTop;
    const currentTranslateY = new DOMMatrix(getComputedStyle(target).transform).m42;
   
    const newTargetTop = targetTop - currentTranslateY + y;
    const newTargetBottom = newTargetTop + targetRect.height;
   
    let constrainedY = y;
    if (newTargetTop < containerTop)     constrainedY = containerTop - (targetTop - currentTranslateY);
    if (newTargetBottom > containerBottom) constrainedY = containerBottom - targetRect.height - (targetTop - currentTranslateY);
   
    if (constrainedY !== y) {
      target.style.transform = `translate(${x}px, ${constrainedY}px)`;
      event.beforeTranslate = [x, constrainedY];
      event.translate       = [x, constrainedY];
      moveableData.onWidgetDrag(event);
      return true;   // caller should return early — boundary was applied
    }
    return false;
  }, [moveableData]);
   
  const handleDragBoundaries = useCallback((e, isGroup = false) => {
    const events = isGroup ? e.events : [e];
    events.forEach(event => {
      const { target, beforeTranslate: [x, y] } = event;
      applyDragBoundary(target, x, y, event);
    });
  }, [applyDragBoundary]);

  return (
    <Moveable
      flushSync={flushSync}
      ables={[ToolBarViewable, GroupToolBarViewable]}
      ref={ref}
      targets={selectedWidgets}
      dragTarget={dragTarget}
      isDragging={isDragging}
      elementGuidelines={elementGuidelines}
      verticalGuidelines={verticalGuidelines}
      horizontalGuidelines={horizontalGuidelines}
      origin={false}
      toolBarViewable={_.size(selectedWidgets) == 1 && !disabled && isVisible}
      groupToolBarViewable={_.size(selectedWidgets) > 1 && !isDragging && !disabled}
      deleteButtonViewable={true}
      preventClickEventOnDrag={false}
      {...moveableConfig}
      pinchable={["rotatable"]}
      zoom={1}
      scrollable={true}
      throttleResize={0}
      keepRatio={false}
      snappable={true}
      snapCenter={true}
      snapGap={true}
      isDisplayInnerSnapDigit={true}
      roundable={false}
      rotationPosition={'right'}

      scrollOptions={{
        container: ".editor_widget_scroll_container",
        threshold: 30,
        throttleTime: 50,
        checkScrollEvent: true
      }}

      onBeforeRenderStart={(e) => {
        moveableData.onBeforeRenderStart(e);
      }}
      onBeforeRenderGroupStart={moveableData.onBeforeRenderGroupStart}
      onDragStart={(e) => {
        dragScrollContainerRef.current = document.querySelector('.editor-page-wrapper'); // cache once
        setDragStartPosition({ x: e.clientX, y: e.clientY });
        setHasMoved(false);
        moveableData.onDragStart(e);
        if(!editMode) e.target.style.cursor = 'grabbing';
      }}
      onDrag={(e) => {
        if (dragStartPosition && !hasMoved) {
          const currentPosition = { x: e.clientX, y: e.clientY };
          const distance = calculateDistance(dragStartPosition, currentPosition);
          
          if (distance > 5) {
            setHasMoved(true);
            setIsDragging(true);
          }
        }

        const { target, beforeTranslate } = e;
        const [x, y] = beforeTranslate;

        // Get the scroll container (cached at drag start — O(1) ref read)
        const scrollContainer = dragScrollContainerRef.current;

        if (scrollContainer) {
          // Use your original working approach but with container bounds
          const containerRect = scrollContainer.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          
          // Get scroll position
          const scrollTop = scrollContainer.scrollTop;
          
          // Convert to document coordinates (like your original working code)
          const containerTop = containerRect.top + scrollTop;
          const containerBottom = containerRect.bottom + scrollTop;
          const targetTop = targetRect.top + scrollTop;
          
          // Get current transform (like your original working code)
          const currentStyle = getComputedStyle(target);
          const matrix = new DOMMatrix(currentStyle.transform);
          const currentTranslateY = matrix.m42;
          
          // Calculate new position (like your original working code)
          const newTargetTop = targetTop - currentTranslateY + y;
          const newTargetBottom = newTargetTop + targetRect.height;
          
          let constrainedY = y;
          
          // Apply both top and bottom boundaries
          if (newTargetTop < containerTop) {
            constrainedY = containerTop - (targetTop - currentTranslateY);
          }
          
          if (newTargetBottom > containerBottom) {
            constrainedY = containerBottom - targetRect.height - (targetTop - currentTranslateY);
          }
          
          // Apply constraints if changed
          if (constrainedY !== y) {
            target.style.transform = `translate(${x}px, ${constrainedY}px)`;
            
            const modifiedEvent = {
              ...e,
              beforeTranslate: [x, constrainedY],
              translate: [x, constrainedY]
            };
            
            moveableData.onWidgetDrag(modifiedEvent);
            return;
          }
        }
        handleDragBoundaries(e, false);
        moveableData.onWidgetDrag(e);
      }}
      onDragEnd={(e) => {
        dragScrollContainerRef.current = null;
        setIsDragging(false);
        moveableData.onWidgetDragEnd(e);

        const droppedWidget = allWidgetsMap.get(e.target.id);
        if (!moveableData.reparentTarget && droppedWidget && isInFlexFlow(droppedWidget)) {
          e.target.style.transform = '';
          const frame = moveableData.getFrame(e.target);
          frame.set('transform', 'translate', '0px, 0px');

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              editorContext.editorSession.moveableManager.current?.updateRect();
            });
          });
        }

        setTimeout(() => {
          setHasMoved(false);
          setDragStartPosition(null);
          if (e.target.style.cursor === 'grabbing') e.target.style.cursor = 'grab';
        }, 0);
      }}
      onDragGroupStart={(e) => {
        dragScrollContainerRef.current = document.querySelector('.editor-page-wrapper'); // cache once
        setIsDragging(true);
        moveableData.onDragGroupStart(e)
        e.target.style.cursor = 'grabbing';
      }}
      onDragGroup={(e) => {
        handleDragBoundaries(e, true);
        moveableData.onDragGroup(e);
      }}
      onDragGroupEnd={(e) => {
        dragScrollContainerRef.current = null;
        setIsDragging(false);
        e.target.style.cursor = 'grab';
        return moveableData.onDragGroupEnd
      }}

      onScaleStart={moveableData.onScaleStart}
      onScale={moveableData.onScale}
      onScaleGroupStart={moveableData.onScaleGroupStart}
      onScaleGroup={moveableData.onScaleGroup}

      onResizeStart={moveableData.onResizeStart}
      onResize={moveableData.onResize}
      onResizeGroupStart={moveableData.onResizeGroupStart}
      onResizeGroup={moveableData.onResizeGroup}

      onRotateStart={moveableData.onRotateStart}
      onRotate={moveableData.onRotate}
      onRotateGroupStart={moveableData.onRotateGroupStart}
      onRotateGroup={moveableData.onRotateGroup}

      onClick={e => {
        const target = e.target;

        var editMode = false;
        // if (target) {
        //   const id = target.id;
        //   if (e.isDouble) {
        //     editMode = true;
        //     dispatch(toggleEditMode(editorSession, id, true));
        //   }
        // }

        if (!editMode) {
          const widgetToolbar = e.inputTarget.closest('.moveable-widget-toolbar');
          if (!widgetToolbar) {
            selecto.current.clickTarget(e.inputEvent, e.inputTarget);
          }
        }
      }}
      onClickGroup={e => {
        selecto.current.clickTarget(e.inputEvent, e.inputTarget);
      }}
      onRenderStart={e => {
        e.datas.prevData = moveableData.getFrame(e.target).get();
      }}
      onRender={e => {
        e.datas.isRender = true;
        editorContext.editorSession.eventBus.emit("render", e);

        var parent = moveableData.reparentTarget;
        if (!parent) {
          return
        }

        const parentWidget = allWidgetsMap.get(parent.id);

        if (parentWidget && isWidgetLocked(allWidgets, parentWidget, breakpoint)) {
          moveableData.reparentTarget = null;
          return;
        }

        var parentRect = parent.getBoundingClientRect();
        const widgetRect = e.target.getBoundingClientRect();

        const visibleArea = parent.querySelector(`#${parent.id}_visible_area.cs_visible_area__content`);
        if (visibleArea) {
          parentRect = visibleArea.getBoundingClientRect();
        }

        const relative = {
          top: widgetRect.top - parentRect.top,
          left: widgetRect.left - parentRect.left
        };

        e.datas.parentRequest = relative;
      }}
      // Redux layout updates run here and in onRenderGroupEnd only (end of gesture), not on each onDrag frame.
      onRenderEnd={e => {
        if (e.target?.dataset?.flexFloorResizing) {
          delete e.target.dataset.flexFloorResizing;
          e.target.style?.removeProperty('min-height');
        }

        if (!e.datas.isRender) {
          return;
        }

        const wasFlexVerticalResize = moveableData._resizeFlexVertical;

        moveableData._cornerResizeRatio = null;
        moveableData._cornerResizeStartWidth = null;
        moveableData._cornerResizeStartHeight = null;
        moveableData._resizeFlexVertical = false;
        moveableData._resizeContentFloor = 0;

        const id = e.target.id;
        const type = e.target.dataset.widgetType;
        const widgetRect = e.target.getBoundingClientRect();

        var frame = moveableData.getFrame(e.target);
        const parent = moveableData.reparentTarget;

        const draggedWidget = allWidgetsMap.get(id);
        if (!parent && draggedWidget && isInFlexFlow(draggedWidget)) {
          frame.set('transform', 'translate', '0px, 0px');
        }

        const parentRect = parent?.getBoundingClientRect();
        const isLiveElementParent = parent?.getAttribute("data-widget-type") === LIVE_ELEMENT_CONTAINER;
        const isMenuParent = parent?.getAttribute("data-widget-type") === MENU;
        const isPageParent = parent?.getAttribute("data-widget-type") === PAGE;
        const editorElement = document.querySelector('.editor_widget_viewer');

        if (parent) {
          const parentWidget = allWidgetsMap.get(parent.id);
          if (parentWidget && isWidgetLocked(allWidgets, parentWidget, breakpoint)) {
            moveableData.reparentTarget = null;
            return;
          }
        }
        const parentType = parent?.getAttribute("data-widget-type");
        const isCarouselParent = parentType === CAROUSEL_CARD;
        
        let updatedX = 0;
        if ((isMenuParent || isCarouselParent)) {
          const editorElement = document.querySelector('.editor_widget_viewer');
          if (editorElement) {
            const isMenuStretched = parent.className.includes('menu_stretched');
            const carouselContainer = isCarouselParent ? parent.parentElement : null;
            const isCarouselStretched = carouselContainer?.className.includes('carousel_stretched');
      
            if (isMenuStretched || isCarouselStretched) {
              const editorWidthStr = getComputedStyle(editorElement).getPropertyValue('--editor-width');
              const editorWidth = parseInt(editorWidthStr, 10);
              
              if (!isNaN(editorWidth)) {
                updatedX = (editorWidth - 1200) / 2;
              }
            }
          }
        }

        actionGroup.start();

        if (isPageParent){
          const editorWidthStr = getComputedStyle(editorElement).getPropertyValue('--editor-width');
          const editorWidth = parseInt(editorWidthStr, 10);

          if (!isNaN(editorWidth) && widgetRect.width > 1200) {
              updatedX = (1200 - editorWidth) / 2;
          }

          if (type === ANCHOR) {
            updatedX = widgetRect.left - parentRect.left
          }
        }

        const isFlexFlowParent = isFlexParent(allWidgetsMap.get(parent?.id));

        if (parent && !isLiveElementParent) {
          if (isFlexFlowParent) {
            frame.set('transform', 'translate', '0px, 0px');
          } else {
            const relative = {
              top: widgetRect.top - parentRect.top,
              left: widgetRect.x - parentRect.x
            };

            if (parent.getAttribute('data-free-size') != null && parent.getAttribute('data-free-size')) {
              e.datas.parentRequest = relative;
            }

            const parentRequest = e.datas.parentRequest;
            frame.set('transform', 'translate', `${parentRequest.left - updatedX}px, ${parentRequest.top}px`)
          }

          if (type === CAROUSEL && parentType === HOTSPOT) {
            allWidgets.forEach(item => {
              const action = item.actions?.[0];
              if (action?.type !== 'carousel' || action.value?.carouselUid !== id) return;
          
              item.actions = [];
          
              if (item.type === TEXT) {
                item.content = item.content.replace(/<a[^>]*>(.*?)<\/a>/g, '$1');
              }
            });
          }          

          const targetWidget = allWidgetsMap.get(id);
          if (targetWidget?.parent != parent?.id) {
            dispatch(attachWidgetToContainer(editorSession, parent.id, id, frame.get()));
          }
        }

        var layout = frame.get();

        const widget = allWidgetsMap.get(id);

        if (wasFlexVerticalResize && type === CONTAINER) {
          const flooredHeight = Math.round(parseFloat(layout.height));
          if (!isNaN(flooredHeight) && flooredHeight > 0) {
            const resolved = resolveFlexLayout(widget, breakpoint, flexBaseDefaults(type));
            const ownFlexProperty = _.get(widget, `properties.${breakpoint}.flexProperty`, {});
            dispatch(updateWidgetProperty(editorSession, id, breakpoint, {
              flexProperty: { ...resolved, ...ownFlexProperty, minHeight: `${flooredHeight}px`, minHeightSource: 'user' },
            }));
          }
          layout = _.omit(layout, 'height');
        }

        const widgetConfig = WidgetRegistry.moveableConfig(editorSession, type);
        if (widgetConfig && widgetConfig.onResize) {
          layout = widgetConfig.onResize(widget, layout, breakpoint);
        }

        var request = { id, layout };

        dispatch(bulkUpdateWidgetLayouts(editorSession, breakpoint, [request]));

        actionGroup.end();

        moveableData.reparentTarget = null;

        if (wasFlexVerticalResize && type === CONTAINER) {
          requestAnimationFrame(() => requestAnimationFrame(() => {
            editorContext.editorSession.moveableManager.current?.updateRect();
          }));
        }
      }}
      onRenderGroupStart={e => {
        e.datas.prevDatas = e.targets.map(target => moveableData.getFrame(target).get());
      }}
      onRenderGroup={e => {
        e.datas.isRender = true;
      }}
      onRenderGroupEnd={e => {
        if (!e.datas.isRender) {
          return;
        }

        const requests = e.targets.map((target, i) => {
          const id = target.id;
          var layout = moveableData.getFrame(target).get();
          return {
            id, layout
          }
        });

        dispatch(bulkUpdateWidgetLayouts(editorSession, breakpoint, requests));
      }}
      onScroll={({ scrollContainer, direction }) => {
        scrollContainer.scrollBy(direction[0] * 20, direction[1] * 20);
      }}
      onScrollGroup={({ scrollContainer, direction }) => {
        scrollContainer.scrollBy(direction[0] * 20, direction[1] * 20);
      }}
    />
  );
});

const ALLOWED_GROUP = [
  TEXT, BUTTON, SHAPE, LINE, IMAGE, VIDEO, HTML, IFRAME, GROUP, RECTANGLE
];

function KeyManager({ editorSession }) {
  const { id } = useParams();

  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);

  const selectedWidgets = useSelectedWidgetSelector(editorContext.editorSession.id);
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const present = useSelector(state => _.get(state, `${editorSession}.present`, {}));
  const { interactive, entityType, widgets } = present || {};
  const parent = _.get(interactive, 'active', '');

  const pages = useSelector((state) => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    var pages = widgets.filter((item) => item.type === PAGE);
    pages = _.orderBy(pages, "order", "desc");
    return pages;
  });

  const selectedFrame = useSelector(state =>
    _.get(state, `${editorSession}.present.selectedPage`, null)
  );

  const workspaceId = useSelector(state => _.get(state, 'workspaces.workspace.item.id', ''));
  const storyboard = useSelector(state => _.get(state, 'storyboards.storyboard.item', ''));  
  const storyboardType = _.get(storyboard, 'type', '');

  /** Page Editor */
  const pageUpdateState = useSelector(state => _.get(state, `pages.page.${id}.update`, false));
  
  /** BrandKit Editor */
  const paletteUpdateState = useSelector(state => _.get(state, `brandKit.components.paletteComponent.${id}.update`, false));

  /** Gating Editor */
  const gatingUpdateState = useSelector((state) => _.get(state, `gatings.gating.${id}.update`, false));

  /** LiveElement Editor */
  const liveElementUpdateState = useSelector(state => _.get(state, `liveElements.liveElement.${id}.update`, false));

  const editorGatingSession = `editors_gating_${id}`;
  const allWidgets = useSelector(state => _.get(state, `${editorContext.editorSession.id}.present.widgets`));

  const formWidgetsLength = entityType === 'GATING' ? allWidgets.filter(widget => {
    return widget.type === FORM
  }).length : 0;
  const progressiveGatingEnabled = useSelector((state) => _.get(state, `gatings.gating.${id}.item.progressive`, false));

  const handleFrameActions = React.useCallback((callback) => {
    if (selectedWidgets.length === 0 && selectedFrame && supportsFrameEditorFeatures(entityType, storyboardType)) {
      callback([selectedFrame]);
    }
  }, [selectedWidgets, selectedFrame, entityType, storyboardType]);

  const move = useCallback((x, y) => {
    const moveableManager = editorContext.editorSession.moveableManager.current;
    if (!_.get(moveableManager, 'props.draggable')) return;
    moveableManager.request("draggable", { deltaX: x, deltaY: y }, true);
  }, [editorContext.editorSession.moveableManager]);

  const keyMap = {
    UNDO: ["command+z", "ctrl+z"],
    REDO: ["command+shift+z", "ctrl+shift+z"],
    SAVE: ["command+s", "ctrl+s"],
    SELECT_ALL: ["command+a", "ctrl+a"],
    DESELECT_ALL: ["escape"],
    MOVE_UP: "up",
    MOVE_DOWN: "down",
    MOVE_LEFT: "left",
    MOVE_RIGHT: "right",
    MOVE_SHIFT_UP: "shift+up",
    MOVE_SHIFT_DOWN: "shift+down",
    MOVE_SHIFT_LEFT: "shift+left",
    MOVE_SHIFT_RIGHT: "shift+right",
    DELETE_WIDGET: ["del", "backspace"],
    COPY: ["command+c", "ctrl+c"],
    CUT: ["command+x", "ctrl+x"],
    PASTE: ["command+v", "ctrl+v"],
    NEW_SECTION_WIDGET: ["shift+n"],
    GROUP_WIDGETS: ["command+g", "ctrl+g"],
    UNGROUP_WIDGET: ["command+shift+g", "ctrl+shift+g"],
    DUPLICATE_WIDGET: ["command+d", "ctrl+d"],
    PAGE_SETTINGS: "shift+p",
    STORYBOARD_SETTINGS: ["command+shift+s", "ctrl+shift+s"],
    PREVIEW_PAGE: ["command+shift+p", "ctrl+shift+p"],
    NEW_TEXT_WIDGET: "shift+t",
  };

  const handlers = {
    UNDO: (e) => {
      dispatch(undo(editorSession));
    },
    REDO: (e) => {
      dispatch(redo(editorSession));
    },
    SAVE: (e) => {
      e.preventDefault();
      /** Saving is in progress */
      if(pageUpdateState || liveElementUpdateState || gatingUpdateState || paletteUpdateState) return;
      
      switch(entityType) {
        case 'PAGE':
          dispatch(updatePage(id, editorSession));
          break;
        case 'PALETTE_COMPONENT':
          dispatch(updatePaletteComponent(id, editorSession));
          break;
        case 'GATING':
          dispatch(updateGating(id, editorGatingSession));
          break;
        case 'LIVE_ELEMENT':
          dispatch(updateLiveElement(id, editorSession));
          break;
        default:
          if(!storyboardRoleService.canEditStoryboard(storyboard)) return;
          dispatch(updatePage(id, editorSession));
          break;
      }
    },
    SELECT_ALL: (e) => {
      e.preventDefault();
      dispatch(selectAllWidgets(editorSession, storyboardType, breakpoint));
    },
    DESELECT_ALL: (e) => {
      e.preventDefault();
      dispatch(selectWidget(editorSession, [])); 
    },
    MOVE_UP: (e) => {
      e.preventDefault();
      move(0, -1);
    },
    MOVE_DOWN: (e) => {
      e.preventDefault();
      move(0, 1);
    },
    MOVE_LEFT: (e) => {
      e.preventDefault();
      move(-1, 0);
    },
    MOVE_RIGHT: (e) => {
      e.preventDefault();
      move(1, 0);
    },
    MOVE_SHIFT_UP: (e) => {
      e.preventDefault();
      move(0, -10);
    },
    MOVE_SHIFT_DOWN: (e) => {
      e.preventDefault();
      move(0, 10);
    },
    MOVE_SHIFT_LEFT: (e) => {
      e.preventDefault();
      move(-10, 0);
    },
    MOVE_SHIFT_RIGHT: (e) => {
      e.preventDefault();
      move(10, 0);
    },
    DELETE_WIDGET: (e) => {
      if (blockDeleteOperation(selectedWidgets)) {
        return;
      }

      const widgets = selectedWidgets.filter(widget => {
        const sectionUid = widget.type === SECTION_COLUMN ? widget.parent : null;
        const columnCount = sectionUid ? allWidgets.filter((w) => w.parent === sectionUid).length : 0;
        if (columnCount === 1){
          return false;
        }

        const template = _.get(widget, 'template');
        if (template) {
          return false;
        }

        if (isWidgetLocked(allWidgets, widget, breakpoint)) {
          return false;
        }

        const moveableConfig = WidgetRegistry.moveableConfig(editorSession, widget);
        return _.get(moveableConfig, 'deletable', true);
      }).map(el => el.uid);

      handleFrameActions((frame) => {
        const currentPage = pages.find(p => p.uid === frame[0]);
        const visiblePageCount = pages.filter(p => _.get(p, 'properties.desktop.visible', true)).length;
        if (
          _.size(pages) >= 2 &&
          currentPage &&
          (!_.get(currentPage, 'properties.desktop.visible', true) || visiblePageCount >= 2)
        ) {
          dispatch(deleteWidgets(editorSession, frame));
        }
      });

      if (_.size(widgets) > 0) {
        dispatch(deleteWidgets(editorSession, widgets));
      }
    },
    COPY: async (e) => {
      if (blockClipboardOperation(selectedWidgets)) {
        return;
      }

      let copyableWidgets = selectedWidgets.filter(widget => {
        const template = _.get(widget, 'template');
        if (template) {
          return false;
        }
        if (widget.type === PAGE) {
          return supportsFrameEditorFeatures(entityType, storyboardType);
        }
        const moveableConfig = WidgetRegistry.moveableConfig(editorSession, widget);
        return _.get(moveableConfig, 'cloneable', true);
      });

      if (
        copyableWidgets.length === 0 &&
        supportsFrameEditorFeatures(entityType, storyboardType) &&
        _.get(present, 'selectedPage')
      ) {
        const selectedPageId = _.get(present, 'selectedPage');
        const pageWidget = allWidgets.find(
          widget => widget.uid === selectedPageId && widget.type === PAGE
        );
        if (pageWidget) {
          copyableWidgets = [pageWidget];
        }
      }

      if (_.size(copyableWidgets) > 0) {
        editorContext.editorSession.eventBus.emit('copy');
        await navigator.clipboard.writeText(JSON.stringify(copyableWidgets.map(el => el.uid)));
      }
    },
    CUT: async (e) => {
      if (blockClipboardOperation(selectedWidgets)) {
        return;
      }

      const pageWidgets = allWidgets.filter(widget => widget.type === PAGE);
      const selectedPageCount = selectedWidgets.filter(widget => widget.type === PAGE).length;
      const allFramesSelected =
        supportsFrameEditorFeatures(entityType, storyboardType) &&
        selectedPageCount > 0 &&
        selectedPageCount === pageWidgets.length;

      if (allFramesSelected) {
        return;
      }

      let copyableWidgets = selectedWidgets.filter(widget => {
        const template = _.get(widget, 'template');
        if (template) {
          return false;
        }
        if (widget.type === PAGE) {
          return supportsFrameEditorFeatures(entityType, storyboardType);
        }
        const moveableConfig = WidgetRegistry.moveableConfig(editorSession, widget);
        return _.get(moveableConfig, 'cloneable', true);
      });

      if (
        copyableWidgets.length === 0 &&
        supportsFrameEditorFeatures(entityType, storyboardType) &&
        _.get(present, 'selectedPage')
      ) {
        const selectedPageId = _.get(present, 'selectedPage');
        const pageWidget = allWidgets.find(
          widget => widget.uid === selectedPageId && widget.type === PAGE
        );
        if (pageWidget) {
          copyableWidgets = [pageWidget];
        }
      }

      if (_.size(copyableWidgets) > 0) {
        editorContext.editorSession.eventBus.emit('cut');
        await navigator.clipboard.writeText(JSON.stringify(copyableWidgets.map(el => el.uid)));
      }
    },
    PASTE: async (e) => {
      // e.preventDefault();
      // editorContext.editorSession.eventBus.emit('paste');
    },
    PREVIEW_PAGE: (e) => {
      e.preventDefault();

      if(entityType === "PAGE" || storyboard.type === "INTERACTIVE_CONTENT") {
        const url = `${process.env.REACT_APP_UI_URL}/app/${workspaceId}/pages/${id}/preview`;
        window.open(url);
      }
    },
    NEW_TEXT_WIDGET: (e) => {
      e.preventDefault();
    },
    NEW_SECTION_WIDGET: (e) => {
      e.preventDefault();
    },
    GROUP_WIDGETS: (e) => {
      e.preventDefault();
      function isSelectionAllowed(selectedWidgets, widgets, entityType) {
        var allowed = true;

        if(selectedWidgets?.length < 2) return false;

        var parents = selectedWidgets.map(el => el.parent).filter(el => el !== null);
        parents = _.uniq(parents);
    
        if (_.size(parents) === 1) {
          var parent = widgets.find(w => w.uid === parents[0]);
          allowed = ![GROUP, CONTENT_CARD].includes(parent.type);
        }
    
        selectedWidgets.forEach(widget => {
          allowed = allowed && _.includes(ALLOWED_GROUP, widget.type) 
            && !(widgets.some(w => w.parent === widget.uid && w.type === HOTSPOT));
        });
    
        return allowed && _.size(parents) === 1;
      }
      
      const allowed = isSelectionAllowed(selectedWidgets, widgets || [], entityType);
      if(allowed) dispatch(createGroupWidget(editorSession, selectedWidgets));
    },
    UNGROUP_WIDGET: (e) => {
      e.preventDefault();
      selectedWidgets.forEach(widget => {
        if (widget.type === GROUP) {
            dispatch(unGroupWidget(editorSession, widget.uid));
        }
      });
    },
    DUPLICATE_WIDGET: (e) => {
      e.preventDefault();

      if (blockClipboardOperation(selectedWidgets)) {
        return;
      }

      if (
        entityType === "GATING" &&
        formWidgetsLength >= 1 &&
        progressiveGatingEnabled
      ) {
        toast(
          <FailureToast heading={`Error`} description={`Progressive gating is enabled. Only one form widget is allowed`} />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
        return;
      }
      
      const pageWidgets = allWidgets.filter(widget => widget.type === PAGE);
      const selectedPageCount = selectedWidgets.filter(widget => widget.type === PAGE).length;
      const activeFrameId = (() => {
        const selectedPage = _.get(present, 'selectedPage', null);
        const activeSegment = _.get(present, 'activeSegment', null);
        return selectedPage !== activeSegment ? activeSegment : selectedPage;
      })();

      let selectedPages = supportsFrameEditorFeatures(entityType, storyboardType)
        ? selectedWidgets.filter(widget => widget.type === PAGE)
        : [];

      if (
        selectedPages.length === 0 &&
        selectedWidgets.length === 0 &&
        _.get(present, 'selectedPage') &&
        supportsFrameEditorFeatures(entityType, storyboardType)
      ) {
        selectedPages = pageWidgets.filter(
          page => page.uid === _.get(present, 'selectedPage')
        );
      }

      if (selectedPages.length > 0 && activeFrameId) {
        dispatch(clonePages(
          editorSession,
          selectedPages.map(page => page.uid),
          activeFrameId,
          'segment'
        ));
      }

      const widgetsByParent = selectedWidgets
        .filter(widget => widget.type !== PAGE)
        .reduce((acc, widget) => {
          if (_.get(widget, 'template') || !widget.parent) {
            return acc;
          }

          const moveableConfig = WidgetRegistry.moveableConfig(editorSession, widget);
          if (!moveableConfig?.cloneable) {
            return acc;
          }

          if (!acc[widget.parent]) {
            acc[widget.parent] = [];
          }
          acc[widget.parent].push(widget.uid);

          return acc;
        }, {});

      Object.entries(widgetsByParent).forEach(([parent, widgetIds]) => {
        dispatch(cloneWidgets(editorSession, parent, widgetIds));
      });
    },
    PAGE_SETTINGS: (e) => {
      e.preventDefault();
    },
    STORYBOARD_SETTINGS: (e) => {
      e.preventDefault();
      if(storyboard?.id && workspaceId) {
        const storyboardUrl = `${process.env.REACT_APP_UI_URL}/app/${workspaceId}/storyboards/${storyboard.id}`;
        window.open(`${storyboardUrl}/website-settings`);
      }
    },
  };

  return (
    <GlobalHotKeys keyMap={keyMap} handlers={handlers} allowChanges={true} />
  );
}


function ReparentIndicator() {
  const editorContext = React.useContext(EditorContext);
  const [state, setState] = useState(null);
  const parent = _.get(state, 'parent.id');
  const editorSession = editorContext.editorSession.id;
  const externalWidgets = [SECTION, CONTENT, HTML, IFRAME, FORM]

  const parentWidget = useWidgetSelector(editorSession, parent);
  const allWidgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`) || []);
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const childWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    const selected = _.get(state, `${editorSession}.present.selected`)
    return widgets.find(item => item.uid === _.head(selected))
  });


  useEffect(() => {
    const handleParentHover = (data) => {
      setState(data);
    };
    const handleParentChange = () => {
      setState(null);
    };
    editorContext.editorSession.eventBus.on("onParentHover", handleParentHover);
    editorContext.editorSession.eventBus.on("onParentChange", handleParentChange);
    return () => {
      editorContext.editorSession.eventBus.off("onParentHover", handleParentHover);
      editorContext.editorSession.eventBus.off("onParentChange", handleParentChange);
    };
  }, []);

  var indicatorStyles = {};
  if (state) {
    indicatorStyles = {
      left: _.get(state, 'rect.left'),
      top: _.get(state, 'rect.top'),
      width: _.get(state, 'rect.width'),
      height: _.get(state, 'rect.height')
    }
  }


  if (parentWidget && isWidgetLocked(allWidgets, parentWidget, breakpoint)) {
    setState(null)
    return;
  }

  return (
    <Portal>
      {(parentWidget) &&
        <div className={styles.reparentIndicator} style={indicatorStyles}>
          <div className={styles.badgeWrap}>
          <div className={styles.badge}>
            {_.get(state, 'gridOut')
              ? "Widget outside guidelines might not be visible to some visitors"
              : `Attach to ${parentWidget?.name}`
            }
          </div>
          </div>
        </div>
      }
    </Portal>
  );
}

const DevicePreviewContainer = ({ editorSession, widgets }) => {
  const scrollRef = React.useRef(null);

  const currentBreakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const breakpoint = currentBreakpoint === 'desktop' ? 'mobile' : 'desktop';

  const widgetsContextValue = useMemo(() => ({
    session: editorSession,
    selector: `${editorSession}.present`,
    shouldZoom: false,
    breakpoint,
    development: true,
    carouselSlide: null,
    createLiveElementContext: (element) => {
      const sessionId = `editors_live_element_${element.uid}`;
      return { session: sessionId, selector: `${sessionId}.present` };
    }
  }), [editorSession, breakpoint]);

  return (
    <div ref={scrollRef} className={cx(styles.devicePreview, 'editor_device_preview', breakpoint)}>
      <WidgetsContext.Provider value={widgetsContextValue}>
        <FormContext.Provider value={{}}>
          <WidgetActions.Provider value={{
            onBeforeExecution: () => { }
          }}>
            <ParallaxContext scrollRef={scrollRef.current}>
              {widgets.map((widget, index) => {
                return <Widgets key={widget.uid} widgetProps={widget} breakpoint={breakpoint} />
              })}
            </ParallaxContext>
          </WidgetActions.Provider>
        </FormContext.Provider>
      </WidgetsContext.Provider>
    </div>
  );
}